/**
 * The 5 Bid Quality questions, in order. Shared by the submission form (the
 * question rows), the score rail and the reviews table (columns) so the three
 * never drift apart. `key` is the metadata field each answer is stored under
 * on the EmployeeKpi entry.
 *
 * `label` is the full sentence and stays the source of truth for the CSV
 * export header. `title` / `hint` are the same sentence split into the ask and
 * the clarification, so the form can lead with a short, scannable question and
 * demote the explanation to helper text instead of running one long line.
 */
export const BID_QUALITY_QUESTIONS = [
  {
    key: "scopeCorrect",
    label: "Scope correct? Did the job match what was bid?",
    title: "Was the scope correct?",
    hint: "Did the job match what was bid?",
    shortLabel: "Scope",
  },
  {
    key: "pricingCorrect",
    label: "Pricing correct? Was the bid priced fairly for the work?",
    title: "Was the pricing correct?",
    hint: "Was the bid priced fairly for the work?",
    shortLabel: "Pricing",
  },
  {
    key: "timeCorrect",
    label: "Time correct? Was the time estimate close to actual?",
    title: "Was the time correct?",
    hint: "Was the time estimate close to actual?",
    shortLabel: "Time",
  },
  {
    key: "customerExpectationsCorrect",
    label: "Customer expectations correct? Did the customer know what to expect?",
    title: "Were customer expectations correct?",
    hint: "Did the customer know what to expect?",
    shortLabel: "Customer Exp.",
  },
  {
    key: "accessCorrect",
    label: "Access correct? Were access notes / property details accurate?",
    title: "Was the access correct?",
    hint: "Were access notes / property details accurate?",
    shortLabel: "Access",
  },
];

/**
 * Points a single "Yes" is worth (20 with 5 questions). Exported so the form's
 * score rail can render one segment per question without re-deriving it.
 */
export const BID_QUALITY_POINTS_PER_YES = 100 / BID_QUALITY_QUESTIONS.length;

/**
 * Job score as a 0-100 number: each "Yes" is worth 100 / question count
 * (20 with 5 questions), "No" is worth 0. Matches the scoring KpiModal's
 * bidAccuracyForm mode already uses, so historical Bid Accuracy averages
 * keep meaning the same thing after the cutover from GHL/Zapier.
 * @param {Record<string, boolean>} answers keyed by BID_QUALITY_QUESTIONS[].key
 */
export const computeBidQualityScore = (answers) => {
  const total = BID_QUALITY_QUESTIONS.length;
  const yesCount = BID_QUALITY_QUESTIONS.filter((q) => answers?.[q.key] === true).length;
  return Math.round(yesCount * (100 / total) * 100) / 100;
};

/**
 * Normalizes an answer as it comes back from the API, where every metadata
 * value is a string ("true"/"false") because the form posts FormData. Returns
 * true / false, or null when the field is absent entirely.
 */
export const readStoredAnswer = (value) => {
  if (value === true || value === "true") return true;
  if (value === false || value === "false") return false;
  return null;
};
